import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ToastrService} from "ngx-toastr";
import {Observable, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';

/** Type of the handleError function returned by HttpErrorHandler.createHandleError */
export type HandleError =
	<T> (operation?: string, result?: T, params?: {
		[key: string]: string | number | boolean | any
	}, errorMsg?: string) => (error: HttpErrorResponse) => Observable<T>;

/** Handles HttpClient errors */
@Injectable()
export class HttpErrorHandler {
	constructor(private toastrService: ToastrService,
							private translateService: TranslateService,
	) {
	}

	/** Create curried handleError function that already knows the service name */
	createHandleError = (serviceName = '') =>
		<T>(operation = 'operation', result = {} as T, params?: {
			[key: string]: string | number | boolean | any
		}, errorMsg?: string) =>
			this.handleError(serviceName, operation, result, params, errorMsg);

	/**
	 * Returns a function that handles Http operation failures.
	 * This error handler lets the app continue to run as if no error occurred.
	 *
	 * @param serviceName = name of the data service that attempted the operation
	 * @param operation - name of the operation that failed
	 * @param result - optional value to return as the observable result
	 * @param params
	 * @param errorMsg
	 */
	handleError<T>(serviceName = '',
								 operation = 'operation',
								 result = {} as T,
								 params?: { [key: string]: string | number | boolean | any },
								 errorMsg?: string) {

		return (error: HttpErrorResponse): Observable<T> => {
			const message = (error.error instanceof ErrorEvent) ?
				error.message :
				`server returned code ${error.status} with body "${error.error.message}"`;
			if (error?.error?.message?.includes("VALIDATION_FAILED")) {
				let errors = error.error.fieldErrors;
				const fieldErrorsArray = Object.values(errors);
				let filename = params?.fileName;
				let errorMessage = "";
				fieldErrorsArray.forEach((element: any) => {
					if (element.message === "File name contains one or more disallowed characters") {
						errorMessage = this.translateService.instant("fileManagement.send.FILE_NAME_INVALID", {fileName: filename});
					} else if (element.message === "MULTIPLE_FILES_NOT_ALLOWED_IN_COMPRESSED") {
						errorMessage = this.translateService.instant("fileManagement.send.MULTIPLE_FILES_NOT_ALLOWED_IN_COMPRESSED", {fileName: filename});
					} else if (element.message === "DIRECTORY_NOT_ALLOWED_IN_COMPRESSED") {
						errorMessage = this.translateService.instant("fileManagement.send.DIRECTORY_NOT_ALLOWED_IN_COMPRESSED", {fileName: filename});
					} else if (element.message === "BAD_FILENAME_IN_COMPRESSED") {
						errorMessage = this.translateService.instant("fileManagement.send.BAD_FILENAME_IN_COMPRESSED", {fileName: filename});
					} else if (element.message === "INVALID_COMPRESSED_FILE") {
						errorMessage = this.translateService.instant("fileManagement.send.INVALID_COMPRESSED_FILE", {fileName: filename});
					} else if (element.message === "ZERO_BYTE_COMPRESSED_FILE") {
						errorMessage = this.translateService.instant("fileManagement.send.ZERO_BYTE_COMPRESSED_FILE_ERROR", {fileName: filename});
					} else if (element.message === "INVALID_BATCH_ID") {
						errorMessage = this.translateService.instant("fileManagement.send.INVALID_BATCH_ID", {fileName: filename});
					} else if (element.code === "INACTIVE_CLIENT_RELATIONSHIP") {
						errorMessage = this.translateService.instant("fileManagement.common.CLIENT_RELATIONSHIP_INACTIVE", {fileName: filename});
					} else if (element.message === "DUPLICATE_BATCH_ID") {
						errorMessage = this.translateService.instant("fileManagement.send.DUPLICATE_BATCH_ID");
					} else if (element.message.toLowerCase().search("duplicate filename found") !== -1) {
						errorMessage = element.message;
					} else if (element.message.search("Duplicate File Content") !== -1) {
						errorMessage = element.message;
					} else if (element.code === "FORMAT_B_FILE_SIZE_RECORD_LENGTH_ERROR") {
						errorMessage = element.message;
					} else if (element.code === "INACTIVE_SENDER") {
						errorMessage = this.translateService.instant("fileManagement.common.INACTIVE_SENDER", {fileName: filename});
					} else if (element.code === "INACTIVE_RECIPIENT") {
						errorMessage = this.translateService.instant("fileManagement.send.INACTIVE_RECIPINET", {fileName: filename});
					} else if (element.message.search("File not found:") !== -1) {
						errorMessage = this.translateService.instant("fileManagement.send.FILE_ID_NOT_FOUND", {fileName: filename});
					} else if (element.message.search("You are not authorized to upload Passthrough files") !== -1) {
						errorMessage = this.translateService.instant("fileManagement.send.PASSTHROUGH_FILE_DISABLED_FOR_CREDITOR", {creditorName: sessionStorage["partnerClientName"]});
					} else if (element.code === "INVALID_CLIENT_RELATIONSHIP") {
						errorMessage = this.translateService.instant("fileManagement.common.CLIENT_RELATIONSHIP_INVALID", {fileName: filename});
					}
				});
				this.toastrService.error("", errorMessage);
			} else if (error.error.message === "DUPLICATE_INTERFACECODE") {
				this.toastrService.error("", this.translateService.instant("product.exception.DUPLICATE_INTERFACECODE"));
				return of();
			} else if (error.error.message === "CLIENT_RELATIONSHIP_NOT_ACTIVE") {
				this.toastrService.error("", this.translateService.instant("interfaces.CREATE_INTERFACE_ERROR"));
				return of();
			} else {
				console.log(message);
				this.toastrService.error("", this.translateService.instant(`product.exception.${error.error.message}`));
			}
			// Let the app keep running by returning a safe result.
			return of(result);
		}
	}
}
